import Link from 'next/link';
import { useEffect } from 'react';
import { BsPencil } from 'react-icons/bs';
import { IoAdd } from 'react-icons/io5';

import { Anchor, Badge, Group, Stack } from '@mantine/core';
import { FLOWS, FlowType } from 'src/frontend/constants/GettingStartedFlows';
import { useSnapshot } from 'valtio';

import { gettingStartedTourState } from '@diagram/state/getting-started-tour';

import { AppName } from 'models';
import { ProjectHelpers } from 'helpers/ProjectHelpers';

import { DashboardPanel } from 'components/dashboard-panel/DashboardPanel';
import cxPanel from 'components/dashboard-panel/DashboardPanel.module.scss';
import { DCIDE } from 'components/logo/DCIDE';
import { ProjectCreateButton } from 'components/project-overview/components';
import { useRecentProjects } from 'hooks/use-recent-projects';
import { DriverService } from 'services/drivers/DriverService';

import { DashboardActionRow } from './DashboardActionRow';
import { DashboardRecentDesignsTable } from './DashboardRecentDesignsTable';
import { DashboardSkeletonBlock } from './DashboardSkeleton';

const NB_SHOWN_PROJECTS = 5;

const DashboardRecentDesigns = ({ app }: { app: AppName }) => {
    const replus = app === AppName.REPLUS;

    const { recentProjects: projects, isLoading } = useRecentProjects({ revalidateOnMount: true });

    const { activeTour } = useSnapshot(gettingStartedTourState);

    const startTour = (flowKey: FlowType) => {
        gettingStartedTourState.activeTour = flowKey;
    };

    useEffect(() => {
        if (!activeTour) {
            return;
        }

        const driver = DriverService.setup({
            tour: activeTour,
            steps: [
                {
                    element: '[data-recent-projects]',
                    popover: {
                        title: "Let's get started",
                        description: 'Create a new design or open an existing one.',
                        showButtons: ['close'],
                        align: 'center',
                    },
                },
            ],
            popoverOffset: 20,
        });

        const timeout = setTimeout(() => {
            driver.drive();
        }, 1000);

        return () => {
            clearTimeout(timeout);
            driver.destroy();
        };
    }, []);

    return (
        <DashboardPanel
            data-recent-projects
            icon={<BsPencil />}
            color="blue"
            title={
                replus ? (
                    <Group gap={8} wrap="nowrap">
                        Recent Designs in
                        <DCIDE height={13} />
                    </Group>
                ) : (
                    'Recent Designs'
                )
            }
            rightSection={
                <Group gap="md">
                    <Anchor component={Link} href={ProjectHelpers.urls.overview()} fw={500} fz="sm">
                        {replus ? 'Open in DCIDE' : 'View All'}
                    </Anchor>
                    <ProjectCreateButton label="New Design" size="xs" />
                </Group>
            }
        >
            {isLoading ? (
                <Stack gap="xs">
                    {Array.from({ length: NB_SHOWN_PROJECTS }, (_, index) => (
                        <DashboardSkeletonBlock key={index} height={40} />
                    ))}
                </Stack>
            ) : projects.length ? (
                <DashboardRecentDesignsTable projects={projects.slice(0, NB_SHOWN_PROJECTS)} />
            ) : (
                <GettingStarted onStartTour={startTour} />
            )}
        </DashboardPanel>
    );
};

const GettingStarted = ({ onStartTour }: { onStartTour: (flowKey: FlowType) => void }) => (
    <Stack gap={0} className={cxPanel.dividedList}>
        <GettingStartedItem
            icon={<IoAdd />}
            color="black"
            variant="filled"
            title="Create your first design"
            description="Start from a blank canvas and lay out your DC power system."
        />
        {Object.values(FLOWS).map((flow) => (
            <GettingStartedItem
                key={flow.tourKey}
                icon={flow.icon}
                color={flow.color}
                title={flow.title}
                description={flow.description}
                showBadge
                onClick={() => onStartTour(flow.tourKey)}
            />
        ))}
    </Stack>
);

const GettingStartedItem = ({
    title,
    showBadge,
    ...props
}: Omit<React.ComponentProps<typeof DashboardActionRow>, 'href'> & { showBadge?: boolean }) => (
    <DashboardActionRow
        href={ProjectHelpers.urls.create()}
        title={
            <Group gap={6}>
                {showBadge && (
                    <Badge size="xs" variant="default" color="gray">
                        Guided tour
                    </Badge>
                )}
                {title}
            </Group>
        }
        {...props}
    />
);

export { DashboardRecentDesigns };
